/* ============================================================
   NORTHSHEEP — cart state on add-to-cart buttons

   Two jobs, both driven by the same read of /cart.js:

   1. "IN YOUR CART" on card quick-add (unchanged behaviour)
      The quick-add bag sits under the favourite heart on mobile cards. Once
      the product is in the cart it fills dark, the same way the heart fills
      when a product is saved, so the two controls behave as a pair.

   2. "ADDED" when the cart already holds ALL of a variant's stock
      Many hides are one of a kind. Before this, once the only one was in the
      cart its button still said "Add to cart", and pressing it produced an
      error ("The maximum quantity of this item is already in your cart").
      Now the button reads "Added to cart" (product page) / "Added" (cards),
      turns the brand brown, and pressing it opens the cart instead.

      Which variants have a limit comes from data-ns-stock on the button
      (snippets/ns-variant-stock-attr.liquid): only variants Shopify tracks
      AND will not oversell. So a product with 4 in stock flips after the 4th
      is in the cart, and anything that can always be re-added never flips.

   Both reflect the CART, not the tap. A class set on click would lie the
   moment anything else changed: removing the item in the drawer, arriving
   with a cart already full, or an add the server refused. Reading /cart.js
   makes the state true by construction, and it survives navigation.

   Costs one small request, and only on pages that have such buttons.
   ============================================================ */
(function () {
  'use strict';

  if (window.__nsCardCartState) return;
  window.__nsCardCartState = true;

  var qty = Object.create(null); // variant id -> quantity in cart
  var loaded = false;

  function strings() { return window.nsCartStateStrings || {}; }

  function cardFields() {
    return document.querySelectorAll('.ns-card-atc form [name="id"]');
  }
  function limitedButtons() {
    return document.querySelectorAll('button[data-ns-stock]');
  }
  function relevant() {
    return cardFields().length > 0 || limitedButtons().length > 0;
  }

  /* The visible label: the button's own text <span>, not the cart icon's
     wrapper and not the loading spinner. */
  function labelOf(btn) {
    var kids = btn.children;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.tagName === 'SPAN' && !k.classList.contains('ns-card-atc__icon') && !k.classList.contains('loading__spinner')) return k;
    }
    return null;
  }

  function stockFor(btn, variantId) {
    var map = btn.__nsStock;
    if (map === undefined) {
      try { map = JSON.parse(btn.getAttribute('data-ns-stock') || 'null'); } catch (e) { map = null; }
      btn.__nsStock = map;
    }
    if (!map || !Object.prototype.hasOwnProperty.call(map, variantId)) return null;
    return map[variantId];
  }

  function variantIdOf(btn) {
    var form = btn.form || btn.closest('form');
    var field = form && form.querySelector('[name="id"]');
    return field ? String(field.value || '') : '';
  }

  /* Write only when something actually changes. The product page watches
     this button for mutations (to re-apply after Dawn re-renders it on a
     variant change); an unconditional write would feed that observer its
     own output forever. */
  function setMaxed(btn, maxed) {
    var label = labelOf(btn);
    var isCard = btn.classList.contains('ns-card-atc__btn');
    var added = isCard ? (strings().inCart || strings().added || 'In cart') : (strings().addedToCart || 'Added to cart');

    if (maxed) {
      if (!btn.classList.contains('is-maxed')) btn.classList.add('is-maxed');
      if (label && label.textContent !== added) {
        // Remember what it said, so it can go back if the item is removed.
        btn.setAttribute('data-ns-label', label.textContent);
        label.textContent = added;
      }
    } else if (btn.classList.contains('is-maxed')) {
      btn.classList.remove('is-maxed');
      // Only restore our own wording. If Dawn has since rewritten the label
      // (e.g. "Sold out" after a variant change), that text is the truth.
      if (label && label.textContent === added) {
        label.textContent = btn.getAttribute('data-ns-label') || (window.variantStrings && window.variantStrings.addToCart) || label.textContent;
      }
      btn.removeAttribute('data-ns-label');
    }
  }

  function paint() {
    if (!loaded) return;

    // 1. card quick-add: in cart at all
    var fields = cardFields();
    /* The mobile copy is a <button form="..."> outside the form (see
       snippets/ns-card-atc.liquid), so it is found by the form id, not inside.
       All of them are gathered in ONE document query per paint and grouped by
       that id. Querying per card was a full-document search for every card —
       ~100 of them per paint on a lazy-loaded grid. */
    var byForm = Object.create(null);
    var linked = fields.length ? document.querySelectorAll('button.ns-card-atc__btn[form]') : [];
    for (var l = 0; l < linked.length; l++) {
      var fid = linked[l].getAttribute('form');
      (byForm[fid] || (byForm[fid] = [])).push(linked[l]);
    }
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      var form = field.form;
      if (!form) continue;
      var cbtns = Array.prototype.slice.call(form.querySelectorAll('.ns-card-atc__btn'));
      // getAttribute, not form.id: the form holds an <input name="id">, which
      // shadows the property and returns that input instead of the id.
      var formId = form.getAttribute('id');
      if (formId && byForm[formId]) cbtns = cbtns.concat(byForm[formId]);
      var on = !!qty[String(field.value || '')];
      for (var b = 0; b < cbtns.length; b++) {
        if (cbtns[b].classList.contains('is-in-cart') !== on) cbtns[b].classList.toggle('is-in-cart', on);
      }
    }

    // 2. any add-to-cart with a stock limit: all of it in the cart
    var btns = limitedButtons();
    for (var j = 0; j < btns.length; j++) {
      var btn = btns[j];
      if (btn.disabled) { setMaxed(btn, false); continue; } // sold out / unavailable: Dawn's state wins
      var id = variantIdOf(btn);
      var max = stockFor(btn, id);
      setMaxed(btn, max !== null && max > 0 && (qty[id] || 0) >= max);
    }
  }

  function refresh() {
    if (!relevant()) return; // nothing on this page to mark
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        qty = Object.create(null);
        (cart.items || []).forEach(function (item) {
          var k = String(item.variant_id);
          qty[k] = (qty[k] || 0) + (item.quantity || 0);
        });
        loaded = true;
        paint();
      })
      .catch(function () { /* leave the buttons as they are rather than guess */ });
  }

  /* An "Added" button opens the cart instead of submitting. Capture phase, so
     it runs before Dawn's product-form ever sees the click — no request, no
     error message. The sticky add-to-cart bar clicks this same button, so it
     is covered too. */
  function onClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('button.is-maxed[data-ns-stock]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open(btn);
    else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  function start() {
    /* The layout prints the cart's item count at render time. An empty cart
       has nothing to mark, so the opening /cart.js request is skipped and the
       state starts as "nothing in the cart" — which is also what the server
       rendered. Every cartUpdate below still refreshes from the server. An
       unknown count (not a number) takes the old path. */
    var count = strings().cartCount;
    var lines = strings().cartLines;
    if (count === 0) {
      loaded = true;
    } else if (lines && lines.length) {
      /* The layout now prints the basket's contents, not just its size, so a
         full cart is answered from the page instead of from the network. The
         opening /cart.js was a session request on every single page view, and
         a cookie set on one of those evicts the page from the back-forward
         cache — the page comes back alive once and is rebuilt every time
         after. Every cartUpdate below still refreshes from the server, so
         this only removes the request that was asking what the server had
         already told us. */
      qty = Object.create(null);
      for (var i = 0; i < lines.length; i++) {
        var k = String(lines[i][0]);
        qty[k] = (qty[k] || 0) + (lines[i][1] || 0);
      }
      loaded = true;
      paint();
    } else {
      refresh();
    }

    /* BACK FROM THE PAGE CACHE (audit, 23 Sep 2026). Safari and Chrome on an
       iPhone hand this page back alive with its buttons exactly as they were:
       a hide added on its product page still offered "Add" here (and then
       failed at the stock limit), one removed on /cart still read "Added".
       Every page stamps sessionStorage when the basket changes
       (ns-cart-upsells.js, and here); if the stamp is newer than what this
       page last saw, ask the server once. No stamp, no request — a request
       can set the cookie that evicts this page from that cache (see above). */
    var seenAt = Date.now();
    function stampCart() {
      seenAt = Date.now();
      try { sessionStorage.setItem('northsheep_cart_changed', String(seenAt)); } catch (e) {}
    }
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var stamp = 0;
      try { stamp = parseInt(sessionStorage.getItem('northsheep_cart_changed'), 10) || 0; } catch (err) {}
      if (stamp <= seenAt) return;
      seenAt = stamp;
      refresh();
    });

    // Dawn publishes this after every add, change and remove, from the drawer
    // and the cart page alike.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        stampCart();
        refresh();
      });
      // Product page: a different variant may have a different limit. Dawn
      // rewrites the button's label and disabled state after this, so repaint
      // a beat later rather than immediately.
      if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, function () { schedulePaint(80); });
    }

    document.addEventListener('click', onClick, true);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') schedulePaint(80);
    });

    /* Debounced, like every other observer in this theme.

       During a curate drag the reorder loop calls grid.insertBefore from
       inside requestAnimationFrame, so childList mutations fire EVERY FRAME
       while a finger is down. paint() walks every card's form — on a 96-card
       grid that is a few hundred DOM queries per frame, exactly when the main
       thread is busiest. 60ms matches the curate and favourites observers. */
    var repaintT = null;
    function schedulePaint(ms) {
      clearTimeout(repaintT);
      repaintT = setTimeout(function () {
        /* A card is in the hand: the grid changes every time it passes a
           neighbour, and a paint then walks every card mid-gesture — felt as
           a hitch just after each crossing. Nothing about the cart changed,
           so paint once the card has been dropped. */
        if (document.body.classList.contains('ns-fav-sorting')) { schedulePaint(250); return; }
        paint();
      }, typeof ms === 'number' ? ms : 60);
    }

    // The grid is rebuilt wholesale by sorting, filtering and the lazy page
    // batches, which drops the classes. Repaint from the state we already
    // hold — no request, same frame the new cards appear.
    document.addEventListener('ns:cards-appended', function () { schedulePaint(); });
    var host = document.getElementById('ProductGridContainer');
    if (host && window.MutationObserver) {
      new MutationObserver(function () { schedulePaint(); }).observe(host, { childList: true, subtree: true });
    }

    // Product page: Dawn re-renders the buy buttons when the variant changes,
    // which can reset our label. paint() only writes on a real change, so this
    // settles after one pass instead of looping.
    // A colour/type swap replaces <main>, and with it the form watched here: watch
    // the new one and paint it (4 Oct 2026 audit: after a swap to a type whose
    // last piece is already in the basket, the button still offered to add it).
    var pdpObserver = null;
    function watchPdp() {
      if (pdpObserver) pdpObserver.disconnect();
      pdpObserver = null;
      var pdp = document.querySelector('product-form button[data-ns-stock]');
      var pdpForm = pdp && pdp.closest('product-form');
      if (pdpForm && window.MutationObserver) {
        pdpObserver = new MutationObserver(function () { schedulePaint(); });
        pdpObserver.observe(pdpForm, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
      }
    }
    watchPdp();
    document.addEventListener('ns:colour-swapped', function () {
      watchPdp();
      schedulePaint();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
